import { LinkField, Text } from "@sitecore-jss/sitecore-jss-nextjs";
import { Field } from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { getStockPriceData, StockPriceData } from "@repo/apis/stockprice";
import { getSummaries, ProductSummaries } from "@repo/apis/summaries";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import * as GridComponent from "@repo/ui/grid";
import * as ProductTile from "@repo/ui/product-tile";
import { SaveForLater } from "@repo/ui/saved-items";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import { removeBrandPrefix } from "@repo/utils/remove-brand-prefix";
import { ProductLink } from "@/components/product-link";
import * as Viewport from "@/components/viewport";
import { COLLAGE } from "@/constants";
import { TYPOGRAPHY_BODY3 } from "@/theme/topshop-2/typography/typography-constants";
import { clientRequest } from "@/utils/request-adapters/client-request";
import { useStore } from "../../../../../ui/src/components/atoms/store/store";
import { ComponentProps } from "../../../types/component-props";
import AnimatedCtaLink from "../../animated-cta-link/animated-cta-link";
import { appendRef } from "../../utils";
import { productSortByStock } from "../utils";
import { ProductGridItem } from "./type";
import styles from "./product-grid.module.css";

export type ProductGridProps = ComponentProps & {
  fields: {
    data: {
      datasource: {
        ctaLink: { jsonValue: LinkField };
        ctaText: Field<string>;
        ctaRef: Field<string>;
        header: Field<string>;
        products: { results: Array<ProductGridItem> };
      };
    };
  };
};

const ProductGrid = (props: ProductGridProps) => {
  const {
    fields: {
      data: {
        datasource: {
          ctaLink: { jsonValue: headerLink },
          ctaText: headerLinkText,
          ctaRef: headerLinkRef,
          header: headerTitle,
          products: { results: products },
        },
      },
    },
  } = props;
  const [stockPriceData, setStockPriceData] = useState<
    (StockPriceData & ProductSummaries)[]
  >([]);

  const {
    shop,
    countryData: {
      storeCode: store,
      keyStoreDataversion,
      countryCode: country,
      defaultLanguage: lang,
      defaultCurrency: currency,
      defaultSizeSchema: sizeSchema,
    },
  } = useStore();

  const ctaLinkWithRef = appendRef(headerLink.value.href!, headerLinkRef.value);

  if (!products.length) {
    return null;
  }

  const fetchProductsData = async () => {
    const productsIdentifiers = products.map((product) => {
      const idField = product.fields.find(
        (f) => f.name === "productIdentifier",
      );
      return +idField!.jsonValue.value;
    });
    try {
      const [stockDataResponse, summariesResponse] = await Promise.all([
        getStockPriceData({
          requestInstance: clientRequest,
          productIds: productsIdentifiers,
          store,
          keyStoreDataversion,
          country,
          lang,
          shop,
        }),
        getSummaries({
          chameleonRequestInstance: clientRequest,
          productIds: productsIdentifiers,
          store,
          keyStoreDataversion,
          lang,
          shop,
          currency,
          sizeSchema,
          expand: ["variants"],
        }),
      ]);

      const stockLookup = Object.fromEntries(
        stockDataResponse.map((item) => [item.productId, item]),
      );
      const summariesLookup = Object.fromEntries(
        summariesResponse!.map((item) => [item.id, item]),
      );

      const combinedData = productsIdentifiers
        .filter((id) => stockLookup[id] && summariesLookup[id])
        .map((id) => ({
          ...stockLookup[id],
          ...summariesLookup[id],
        }));
      setStockPriceData(combinedData as (StockPriceData & ProductSummaries)[]);
    } catch (error) {
      console.error("Error fetching stock data:", error);
      return;
    }
  };

  useEffect(() => {
    if (products.length > 0) {
      fetchProductsData();
    }
  }, [products]);

  const gridItems = useMemo(() => {
    if (!stockPriceData?.length) return [];
    return productSortByStock(stockPriceData).map(
      ({ name, brandName, images, productPrice, id }, index) => {
        const imageUrl =
          images && images.length > 0 && images[0]?.url
            ? prependUrlWithHttps(images[0].url)
            : "";
        if (!name || !imageUrl || !productPrice?.current?.text) {
          return <></>;
        }
        const productName = removeBrandPrefix({
          brand: brandName,
          text: name,
        });
        return (
          <ProductLink productName={name} productId={id} key={`${index}-${id}`}>
            <Flex.Container column gap="sm" className={styles.gridFeatureItem}>
              <ProductTile.ProductImage
                imageUrl={imageUrl}
                altText={productName}
                sourceSetProfile={COLLAGE}
                srcSize={240}
              />
              <Flex.Container className={styles.textContainer}>
                <Flex.Item grow={1}>
                  <Flex.Container column>
                    <ProductTile.Title className={clsx(TYPOGRAPHY_BODY3)}>
                      {productName}
                    </ProductTile.Title>
                    <ProductTile.Price
                      className={clsx(TYPOGRAPHY_BODY3)}
                      data-testid="product-price"
                      priceData={productPrice}
                      country={country}
                      currency={currency}
                      previousPriceCopy="Previous Price"
                      currentPriceCopy="Current Price"
                    />
                  </Flex.Container>
                </Flex.Item>
                <Flex.Item>
                  <SaveForLater
                    productId={id}
                    colourWayId={images[0]!.colourWayId ?? undefined}
                    className={styles.saveForLaterMobile}
                  />
                </Flex.Item>
              </Flex.Container>
            </Flex.Container>
          </ProductLink>
        );
      },
    );
  }, [stockPriceData]);

  return (
    <Container.Root size="medium">
      <Flex.Container
        justify="space-between"
        align="center"
        className={styles.header}
      >
        <Text field={headerTitle} className="typographyHeading1" tag="p" />
        <AnimatedCtaLink
          className="typographyLink2Caps"
          link={{
            ...headerLink,
            value: {
              ...headerLink.value,
              href: ctaLinkWithRef,
              text: headerLinkText?.value || "Default Link Text",
            },
          }}
        />
      </Flex.Container>
      <Viewport.Small>
        <GridComponent.Container
          data-testid="mobile-grid"
          columns={2}
          rowGap={"md"}
        >
          {gridItems}
        </GridComponent.Container>
      </Viewport.Small>
      <Viewport.Large data-testid="desktop-grid">
        <GridComponent.Container
          columns={gridItems.length <= 4 ? gridItems.length : 4}
        >
          {gridItems}
        </GridComponent.Container>
      </Viewport.Large>
    </Container.Root>
  );
};

export default ProductGrid;
